/**
 * Spill Ready Supplies — Quote / PO PDF generator
 * Injects a "Download Quote / PO PDF" button into the EA Sticky Cart drawer
 * and builds a PDF from the CURRENT Shopify cart via the standard /cart.js AJAX API.
 *
 * This script does not modify cart behavior, does not create orders/drafts,
 * and does not touch EA Sticky Cart's own markup other than inserting one
 * sibling element inside its footer.
 */
(function () {
  'use strict';

  var SEL = {
    root: '#ea-sticky-cart-root',
    bottom: '.eascBottomOfMiniCart',
    checkoutWrap: '.easc-miniCartCheckoutDiv',
    title: '.easc-miniCartTitle'
  };

  var WRAP_ID = 'srs-quote-pdf-wrap';
  var BTN_ID = 'srs-quote-pdf-btn';
  var PO_INPUT_ID = 'srs-po-input';
  var STYLE_ID = 'srs-quote-pdf-style';

  var JSPDF_URL = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
  var AUTOTABLE_URL = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js';

  // Variants explicitly on the "FREE SHIPPING ON SELECTED ITEM" delivery
  // profile (unconditional $0 rate, no minimum order price).
  var FREE_SHIP_VARIANT_IDS = [
    48562477793532, 48633886900476, 48633886933244, 48633886966012,
    48633886998780, 48633887031548, 48633887064316, 48633887097084,
    48633887129852, 48633887162620, 48633887195388, 48633887228156,
    48633887293692, 49165823836412, 49165824786684, 49165824917756,
    49165825147132, 49165825343740, 49165825638652, 49165825802492,
    49165825966332, 49165826064636, 49165826195708, 49165826392316,
    49165826621692, 49165827244284, 49165827375356, 49165827997948,
    49165828129020, 49165828227324, 49226159849724
  ];

  // Variants on the individual heavy-freight delivery profiles (LTL pallets:
  // SDP-IBC53, SDP-4D23Y, SDP-2D24Y, SDP-4D45Y, SDP-IBC62Y, SDT-09). Each of
  // these profiles has weight-tiered calculated freight rates PLUS a $0 rate
  // that kicks in once the order total hits $3,000.
  var FREIGHT_VARIANT_IDS = [
    48562478088444, 48562477859068, 48562477891836, 48562477924604,
    48562477990140, 48633886802172
  ];

  // The "Sample Kit" profile (Shopify sample box) ships for $9.99 below $10
  // and free at/above $10 — in practice this is free on any real order.
  var SAMPLE_KIT_VARIANT_IDS = [49077046968572];

  // Thresholds pulled directly from Shopify Admin > Settings > Shipping and
  // delivery (verified live via the Admin API, not guessed):
  //   General profile "Economy" rate:             $0 at order total >= $499
  //   Freight profiles (SDP-*) "TOTAL_PRICE" rate: $0 at order total >= $3,000
  //   Sample Kit profile:                          $0 at order total >= $10
  // NOTE: this is a snapshot as of 2026-08-12 — if these are changed in
  // Shopify Admin, these constants/lists need to be updated to match.
  var GENERAL_FREE_THRESHOLD_CENTS = 49900;
  var FREIGHT_FREE_THRESHOLD_CENTS = 300000;
  var SAMPLE_KIT_FREE_THRESHOLD_CENTS = 1000;

  // Whether a single line item ships free given the CURRENT cart total.
  // Shopify evaluates each shipping profile's TOTAL_PRICE condition against
  // the whole order's total, not just that profile's share of the cart, so
  // every branch below is checked against the same cartTotal.
  function isItemFree(item, cartTotal) {
    if (FREE_SHIP_VARIANT_IDS.indexOf(item.variant_id) !== -1) return true;
    if (FREIGHT_VARIANT_IDS.indexOf(item.variant_id) !== -1) {
      return cartTotal >= FREIGHT_FREE_THRESHOLD_CENTS;
    }
    if (SAMPLE_KIT_VARIANT_IDS.indexOf(item.variant_id) !== -1) {
      return cartTotal >= SAMPLE_KIT_FREE_THRESHOLD_CENTS;
    }
    // General / default profile
    return cartTotal >= GENERAL_FREE_THRESHOLD_CENTS;
  }

  function computeShipping(cart) {
    var items = cart.items || [];
    if (!items.length) return { free: false, label: 'Calculated at checkout' };

    // Use total_price (post cart/line-item discounts, pre shipping/tax) since
    // that's what these free-shipping conditions are actually evaluated
    // against at checkout — not the pre-discount items_subtotal_price.
    var cartTotal = cart.total_price;
    var allFree = items.every(function (i) { return isItemFree(i, cartTotal); });

    return allFree ? { free: true, label: 'Free' } : { free: false, label: 'Calculated at checkout' };
  }

  /* ---------------------------------------------------------------------
   * Small helpers
   * ------------------------------------------------------------------- */

  function pad2(n) { return n < 10 ? '0' + n : '' + n; }

  function generateQuoteNumber() {
    var d = new Date();
    var stamp = d.getFullYear() + pad2(d.getMonth() + 1) + pad2(d.getDate());
    var rand = Math.floor(1000 + Math.random() * 9000);
    return 'QUOTE-' + stamp + '-' + rand;
  }

  function formatMoney(cents) {
    var value = (Math.round(cents) / 100).toFixed(2);
    var parts = value.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + parts.join('.');
  }

  function loadScriptOnce(src) {
    return new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[src="' + src + '"]');
      if (existing) {
        if (existing.getAttribute('data-loaded') === 'true') return resolve();
        existing.addEventListener('load', function () { resolve(); });
        existing.addEventListener('error', reject);
        return;
      }
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = function () {
        s.setAttribute('data-loaded', 'true');
        resolve();
      };
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  var libsPromise = null;
  function ensureLibs() {
    if (!libsPromise) {
      libsPromise = loadScriptOnce(JSPDF_URL).then(function () {
        return loadScriptOnce(AUTOTABLE_URL);
      });
    }
    return libsPromise;
  }

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('Cart fetch failed: ' + r.status);
      return r.json();
    });
  }

  function currentItemCountFromDom() {
    var titleEl = document.querySelector(SEL.title);
    if (!titleEl) return null;
    var match = (titleEl.textContent || '').match(/(\d+)/);
    return match ? parseInt(match[1], 10) : null;
  }

  // Fetches the store logo and converts it to a data URL + intrinsic size so
  // it can be embedded in the PDF via jsPDF's addImage (which needs a data
  // URL / raw image data, not a remote URL). Resolves to null on any failure
  // so a missing/unreachable logo never blocks PDF generation — the header
  // just falls back to the plain-text store name.
  function loadLogoImage(url) {
    if (!url) return Promise.resolve(null);
    return fetch(url, { mode: 'cors' })
      .then(function (r) {
        if (!r.ok) throw new Error('Logo fetch failed: ' + r.status);
        return r.blob();
      })
      .then(function (blob) {
        return new Promise(function (resolve, reject) {
          var reader = new FileReader();
          reader.onload = function () { resolve(reader.result); };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      })
      .then(function (dataUrl) {
        return new Promise(function (resolve, reject) {
          var img = new Image();
          img.onload = function () {
            resolve({ dataUrl: dataUrl, width: img.naturalWidth, height: img.naturalHeight });
          };
          img.onerror = reject;
          img.src = dataUrl;
        });
      })
      .catch(function () { return null; });
  }

  var logoPromise = null;
  function ensureLogo() {
    if (!logoPromise) {
      var url = window.srsStoreInfo && window.srsStoreInfo.logoUrl;
      logoPromise = loadLogoImage(url);
    }
    return logoPromise;
  }

  /* ---------------------------------------------------------------------
   * PDF generation
   * ------------------------------------------------------------------- */

  function lineItemName(item) {
    var name = item.product_title || item.title || 'Item';
    if (item.variant_title && item.product_has_only_default_variant === false) {
      name += ' — ' + item.variant_title;
    }
    return name;
  }

  // Absolute product-page URL for a cart line item, used to make each row
  // in the PDF clickable. cart.js returns item.url as a store-relative path
  // (e.g. "/products/foo-bar?variant=123"); resolve it against the shop
  // domain so the link works when the PDF is opened outside the browser.
  function absoluteProductUrl(item) {
    if (!item || !item.url) return null;
    if (/^https?:\/\//i.test(item.url)) return item.url;
    var origin = (window.srsStoreInfo && window.srsStoreInfo.domain) || window.location.origin;
    origin = origin.replace(/\/+$/, '');
    var path = item.url.indexOf('/') === 0 ? item.url : '/' + item.url;
    return origin + path;
  }

  // Some stores have HTML entities baked into the raw Shop "Name" setting
  // (e.g. "Foo &amp; Bar | Brand") and use a "SEO Title | Brand" convention.
  // Decode entities and prefer the trailing segment as a clean display name.
  function cleanStoreName(raw) {
    if (!raw) return '';
    var decoded = String(raw)
      .replace(/&amp;/g, '&')
      .replace(/&#39;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&nbsp;/g, ' ');
    var parts = decoded.split('|');
    return (parts.length > 1 ? parts[parts.length - 1] : decoded).trim();
  }

  function buildPdf(cart, poNumber, logo) {
    var jsPDFCtor = window.jspdf && window.jspdf.jsPDF;
    if (!jsPDFCtor) throw new Error('jsPDF did not load');

    var doc = new jsPDFCtor({ unit: 'pt', format: 'letter' });
    var pageWidth = doc.internal.pageSize.getWidth();
    var pageHeight = doc.internal.pageSize.getHeight();
    var margin = 40;
    var store = window.srsStoreInfo || {};
    var customer = window.srsCustomer || null;
    var quoteNumber = generateQuoteNumber();
    var quoteDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
    var shipping = computeShipping(cart);

    var y = margin;

    // ---- Header: store identity (left) ----
    // The logo file is the full brand lockup (icon + "Spill Ready Supplies"
    // wordmark + tagline), so it stands alone as the header — no need to
    // duplicate the store name as separate text next to it.
    var storeName = cleanStoreName(store.name) || document.title || 'Store';
    if (logo && logo.dataUrl) {
      var logoH = 40;
      var logoW = logo.width && logo.height ? logoH * (logo.width / logo.height) : 114;
      logoW = Math.min(logoW, 180); // guard against unusually wide/narrow source images
      var fmtMatch = /^data:image\/(\w+);/.exec(logo.dataUrl);
      var fmt = fmtMatch ? fmtMatch[1].toUpperCase() : 'PNG';
      if (fmt === 'JPG') fmt = 'JPEG';
      doc.addImage(logo.dataUrl, fmt, margin, y, logoW, logoH);
      y += logoH + 10;
    } else {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(20, 20, 20);
      doc.text(storeName, margin, y);
      y += 18;
    }

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);

    var addressLine1 = [store.address1, store.address2].filter(Boolean).join(' ');
    var addressLine2 = [store.city, store.province, store.zip].filter(Boolean).join(', ');
    var contactLines = [];
    if (addressLine1) contactLines.push(addressLine1);
    if (addressLine2) contactLines.push(addressLine2);
    if (store.phone) contactLines.push('Tel: ' + store.phone);
    if (store.email) contactLines.push(store.email);
    if (store.domain) contactLines.push(store.domain.replace(/^https?:\/\//i, ''));

    contactLines.forEach(function (line) {
      doc.text(line, margin, y);
      y += 12;
    });

    var headerLeftBottom = y;

    // ---- Header: document identity (right) ----
    var rightY = margin;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.setTextColor(20, 20, 20);
    doc.text('QUOTE / PURCHASE ORDER', pageWidth - margin, rightY, { align: 'right' });
    rightY += 18;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.text('Quote #: ' + quoteNumber, pageWidth - margin, rightY, { align: 'right' });
    rightY += 14;
    doc.text('Date: ' + quoteDate, pageWidth - margin, rightY, { align: 'right' });
    rightY += 14;
    if (poNumber) {
      doc.text('PO / Ref #: ' + poNumber, pageWidth - margin, rightY, { align: 'right' });
      rightY += 14;
    }

    y = Math.max(headerLeftBottom, rightY) + 16;

    doc.setDrawColor(225, 225, 225);
    doc.setLineWidth(1);
    doc.line(margin, y, pageWidth - margin, y);
    y += 22;

    // ---- Customer block ----
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(140, 140, 140);
    doc.text('PREPARED FOR', margin, y);
    y += 14;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(30, 30, 30);
    if (customer && (customer.name || customer.email)) {
      if (customer.name) { doc.text(String(customer.name), margin, y); y += 13; }
      if (customer.company) { doc.text(String(customer.company), margin, y); y += 13; }
      if (customer.email) { doc.text(String(customer.email), margin, y); y += 13; }
    } else {
      doc.setTextColor(120, 120, 120);
      doc.text('Guest customer', margin, y);
      y += 13;
    }
    y += 14;

    // ---- Items table ----
    var rows = cart.items.map(function (item) {
      return [
        lineItemName(item),
        item.sku ? item.sku : '—',
        String(item.quantity),
        formatMoney(item.original_price),
        formatMoney(item.original_line_price)
      ];
    });

    // Parallel array of product-page URLs, keyed by row index, so each
    // product name in the table can be made a clickable link.
    var productLinks = cart.items.map(function (item) {
      return absoluteProductUrl(item);
    });

    doc.autoTable({
      startY: y,
      margin: { left: margin, right: margin },
      head: [['Product', 'SKU', 'Qty', 'Unit Price', 'Line Total']],
      body: rows,
      styles: {
        font: 'helvetica',
        fontSize: 9,
        cellPadding: 6,
        overflow: 'linebreak',
        lineColor: [230, 230, 230],
        lineWidth: 0.5
      },
      headStyles: {
        fillColor: [26, 26, 26],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9
      },
      alternateRowStyles: { fillColor: [250, 250, 250] },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 85 },
        2: { cellWidth: 34, halign: 'center' },
        3: { cellWidth: 68, halign: 'right' },
        4: { cellWidth: 72, halign: 'right' }
      },
      didParseCell: function (data) {
        // Style the product-name column like a hyperlink when it has a URL.
        if (data.section === 'body' && data.column.index === 0 && productLinks[data.row.index]) {
          data.cell.styles.textColor = [37, 99, 235];
        }
      },
      didDrawCell: function (data) {
        // Overlay an actual clickable link annotation on the product-name cell.
        if (data.section === 'body' && data.column.index === 0) {
          var url = productLinks[data.row.index];
          if (url) {
            doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, { url: url });
          }
        }
      },
      didDrawPage: function () {
        var pageNum = doc.internal.getNumberOfPages();
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(160, 160, 160);
        doc.text('Page ' + pageNum, pageWidth - margin, pageHeight - 24, { align: 'right' });
        doc.text(quoteNumber, margin, pageHeight - 24);
      }
    });

    var finalY = doc.lastAutoTable.finalY + 24;

    // Page-break guard for the totals block + Buy Now button + footer
    if (finalY > pageHeight - 230) {
      doc.addPage();
      finalY = margin + 10;
    }

    // ---- Totals ----
    var totalsWidth = 220;
    var totalsX = pageWidth - margin - totalsWidth;

    function totalsRow(label, value, opts) {
      opts = opts || {};
      doc.setFont('helvetica', opts.bold ? 'bold' : 'normal');
      doc.setFontSize(opts.bold ? 11 : 10);
      doc.setTextColor.apply(doc, opts.color || [30, 30, 30]);
      doc.text(label, totalsX, finalY);
      doc.text(value, totalsX + totalsWidth, finalY, { align: 'right' });
      finalY += opts.bold ? 20 : 15;
    }

    totalsRow('Subtotal', formatMoney(cart.items_subtotal_price));

    if (cart.total_discount && cart.total_discount > 0) {
      totalsRow('Discount', '-' + formatMoney(cart.total_discount), { color: [190, 40, 40] });
    }

    totalsRow('Shipping', shipping.label, { color: shipping.free ? [26, 130, 60] : [130, 130, 130] });
    totalsRow('Estimated Tax', 'Calculated at checkout', { color: [130, 130, 130] });

    doc.setDrawColor(210, 210, 210);
    doc.line(totalsX, finalY - 6, totalsX + totalsWidth, finalY - 6);
    finalY += 6;

    totalsRow('Grand Total*', formatMoney(cart.total_price), { bold: true });

    finalY += 8;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7.5);
    doc.setTextColor(140, 140, 140);
    var footnote = shipping.free
      ? '*Excludes tax, calculated at checkout. Shipping eligibility subject to final order review.'
      : '*Excludes shipping and tax, calculated at checkout.';
    doc.text(footnote, totalsX, finalY, { maxWidth: totalsWidth });

    // ---- Buy Now button ----
    // Links to Shopify's cart permalink (/cart/{variant}:{qty},...), which
    // adds every line item in this PO to the customer's cart and lands them
    // on the cart page, where they can click through to checkout themselves.
    finalY += 20;
    var buyBtnW = totalsWidth;
    var buyBtnH = 30;
    var buyBtnX = totalsX;
    var storeOrigin = (store.domain || window.location.origin).replace(/\/+$/, '');
    var permalinkItems = cart.items
      .map(function (item) { return item.variant_id + ':' + item.quantity; })
      .join(',');
    var buyNowUrl = storeOrigin + '/cart/' + permalinkItems;

    doc.setFillColor(255, 187, 4);
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.75);
    doc.roundedRect(buyBtnX, finalY, buyBtnW, buyBtnH, 4, 4, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(20, 20, 20);
    doc.text('Buy Now — Add to Cart', buyBtnX + buyBtnW / 2, finalY + buyBtnH / 2 + 4, { align: 'center' });
    doc.link(buyBtnX, finalY, buyBtnW, buyBtnH, { url: buyNowUrl });
    finalY += buyBtnH + 10;

    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7.5);
    doc.setTextColor(140, 140, 140);
    doc.text(
      'Adds these items to your cart so you can review and check out on our site.',
      buyBtnX, finalY, { maxWidth: buyBtnW }
    );

    // ---- Footer disclaimer (bottom of last page) ----
    var footerY = pageHeight - 56;
    doc.setDrawColor(230, 230, 230);
    doc.line(margin, footerY - 12, pageWidth - margin, footerY - 12);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(140, 140, 140);
    doc.text(
      'This document is a price quote based on current cart contents and is not an invoice or order confirmation.',
      margin, footerY, { maxWidth: pageWidth - margin * 2 }
    );
    doc.text(
      'Prices, availability, and applicable discounts are subject to change until an order is placed at checkout.',
      margin, footerY + 11, { maxWidth: pageWidth - margin * 2 }
    );
    var contactBits = [store.email, store.phone].filter(Boolean).join('  |  ');
    if (contactBits) {
      doc.text('Questions? ' + contactBits, margin, footerY + 22);
    }

    doc.save(quoteNumber + '.pdf');
  }

  /* ---------------------------------------------------------------------
   * Button injection + EA Sticky Cart integration
   * ------------------------------------------------------------------- */

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent =
      '#' + WRAP_ID + '{margin:10px 0;}' +
      '#' + WRAP_ID + ' .srs-po-field{margin-bottom:8px;}' +
      '#' + WRAP_ID + ' .srs-po-field label{display:block;font-family:inherit;' +
      'font-size:11px;font-weight:600;color:#555;margin-bottom:4px;}' +
      '#' + WRAP_ID + ' .srs-po-field input{width:100%;box-sizing:border-box;' +
      'padding:9px 11px;border:1px solid #cfcfcf;border-radius:5px;font-family:inherit;' +
      'font-size:13px;color:#1a1a1a;background:#fff;}' +
      '#' + WRAP_ID + ' .srs-po-field input:focus{outline:none;border-color:#1a1a1a;}' +
      '#' + BTN_ID + '{width:100%;display:flex;align-items:center;justify-content:center;' +
      'gap:8px;padding:12px 16px;background:#FFBB04;border:1px solid #000000;' +
      'border-radius:5px;color:#1a1a1a;font-family:inherit;font-size:14px;' +
      'font-weight:600;line-height:1.1;cursor:pointer;transition:background .15s ease;}' +
      '#' + BTN_ID + ':hover:not(:disabled){background:#f0b100;}' +
      '#' + BTN_ID + ':disabled{opacity:.45;cursor:not-allowed;}' +
      '#' + BTN_ID + ' svg{width:17px;height:17px;flex-shrink:0;}';
    document.head.appendChild(style);
  }

  function createButtonWrap() {
    var wrap = document.createElement('div');
    wrap.id = WRAP_ID;

    var poField = document.createElement('div');
    poField.className = 'srs-po-field';

    var poLabel = document.createElement('label');
    poLabel.setAttribute('for', PO_INPUT_ID);
    poLabel.textContent = 'PO / Reference Number (optional)';

    var poInput = document.createElement('input');
    poInput.type = 'text';
    poInput.id = PO_INPUT_ID;
    poInput.maxLength = 40;
    poInput.placeholder = 'e.g. PO-45821';
    poInput.autocomplete = 'off';
    poInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') e.preventDefault();
    });

    poField.appendChild(poLabel);
    poField.appendChild(poInput);

    var btn = document.createElement('button');
    btn.id = BTN_ID;
    btn.type = 'button';
    btn.setAttribute('aria-label', 'Download Quote / PO PDF for items in your cart');
    btn.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true">' +
      '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill="#E02424"/>' +
      '<path d="M14 2v6h6" fill="none" stroke="#B71C1C" stroke-width="1.25" ' +
      'stroke-linecap="round" stroke-linejoin="round"/>' +
      '<path d="M8.5 12.5h7" stroke="#ffffff" stroke-width="1.5" stroke-linecap="round"/>' +
      '<path d="M8.5 16h5" stroke="#ffffff" stroke-width="1.5" stroke-linecap="round"/>' +
      '</svg>' +
      '<span>Download Quote / PO PDF</span>';
    btn.addEventListener('click', onButtonClick);

    wrap.appendChild(poField);
    wrap.appendChild(btn);
    return wrap;
  }

  function onButtonClick(evt) {
    evt.preventDefault();
    var btn = document.getElementById(BTN_ID);
    if (!btn || btn.disabled) return;
    var label = btn.querySelector('span');
    var originalText = label ? label.textContent : '';
    var poInput = document.getElementById(PO_INPUT_ID);
    var poNumber = poInput ? poInput.value.trim().slice(0, 40) : '';
    btn.disabled = true;
    if (label) label.textContent = 'Preparing PDF…';

    Promise.all([ensureLibs(), fetchCart(), ensureLogo()])
      .then(function (results) {
        var cart = results[1];
        var logo = results[2];
        if (!cart.item_count) {
          refreshButtonState(cart);
          return;
        }
        buildPdf(cart, poNumber, logo);
      })
      .catch(function (err) {
        // eslint-disable-next-line no-console
        console.error('[Quote PDF] Generation failed:', err);
        window.alert('Sorry, we could not generate the quote PDF right now. Please try again in a moment.');
      })
      .finally(function () {
        btn.disabled = false;
        if (label) label.textContent = originalText;
      });
  }

  function refreshButtonState(cartMaybe) {
    var wrap = document.getElementById(WRAP_ID);
    var btn = document.getElementById(BTN_ID);
    if (!wrap || !btn) return;
    var count = cartMaybe && typeof cartMaybe.item_count === 'number'
      ? cartMaybe.item_count
      : currentItemCountFromDom();
    var empty = count === 0;
    wrap.style.display = empty ? 'none' : '';
    btn.disabled = empty;
  }

  function inject() {
    var bottom = document.querySelector(SEL.bottom);
    if (!bottom) return;
    if (document.getElementById(WRAP_ID)) {
      refreshButtonState();
      return;
    }
    injectStyles();
    var checkoutWrap = bottom.querySelector(SEL.checkoutWrap);
    var wrap = createButtonWrap();
    if (checkoutWrap && checkoutWrap.parentElement === bottom) {
      bottom.insertBefore(wrap, checkoutWrap);
    } else {
      bottom.appendChild(wrap);
    }
    refreshButtonState();
  }

  function startObserver(root) {
    inject();
    var debounceTimer = null;
    var observer = new MutationObserver(function () {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(inject, 60);
    });
    observer.observe(root, { childList: true, subtree: true, characterData: true });
  }

  function waitForRootAndObserve() {
    var root = document.getElementById('ea-sticky-cart-root');
    if (root) {
      startObserver(root);
      return;
    }
    var tries = 0;
    var maxTries = 60; // ~15s at 250ms
    var iv = setInterval(function () {
      tries++;
      root = document.getElementById('ea-sticky-cart-root');
      if (root || tries >= maxTries) {
        clearInterval(iv);
        if (root) startObserver(root);
      }
    }, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForRootAndObserve);
  } else {
    waitForRootAndObserve();
  }
})();
